import { z } from 'zod';
import { IsoDateTime, Uuid } from './common';

const Centavos = z.number().int().min(0).max(100_000_000);
const Bps = z.number().int().min(0).max(10_000);
const fiveDecimals = (v: number) => Math.abs(v * 1e5 - Math.round(v * 1e5)) < 1e-6;
export const ReaisPerKwh = z
  .number()
  .min(0)
  .max(100)
  .refine(fiveDecimals, 'Use at most 5 decimals');

export const TaxRegime = z.enum(['mei', 'simples', 'pf']);
export const TaxMode = z
  .enum(['fixed', 'percent'])
  .describe('`fixed`: monthly DAS (MEI); `percent`: % of revenue (Simples).');
export const Bandeira = z.enum(['verde', 'amarela', 'vermelha1', 'vermelha2']);
export const Rounding = z.enum(['end_90', 'end_99', 'whole', 'none']);

const text = (max = 200) => z.string().trim().max(max).nullable();

export const BusinessSettings = z.object({
  shopName: text(120),
  cnpj: text(20),
  regime: TaxRegime.nullable(),
  address: text(),
  city: text(120),
  whatsapp: text(30),
  contactEmail: z
    .email()
    .nullable()
    .or(z.literal('').transform(() => null)),
  instagram: text(60),
  pixKey: text(120).describe('Printed on the quote PDF ("Pagamento por Pix").'),
});

/** Cost parameters (Admin only; every change needs a reason and goes to the history). */
export const CostSettings = z.object({
  energyTariffPerKwh: ReaisPerKwh.nullable().describe('Tariff with taxes, R$/kWh.'),
  bandeira: Bandeira,
  bandeiraPerKwh: ReaisPerKwh.describe('Current bandeira surcharge, R$/kWh (0 for Verde).'),
  laborRatePerHourCentavos: Centavos.nullable().describe('Acabamento e embalagem.'),
  designRatePerHourCentavos: Centavos.nullable().describe('Projeto (modelagem).'),
  failureBps: Bps.nullable(),
  wasteBps: Bps.nullable(),
  targetMarginBps: Bps.nullable(),
  minimumMarginBps: Bps.nullable(),
  taxMode: TaxMode.nullable(),
  dasMonthlyCentavos: Centavos.nullable(),
  taxBps: Bps.nullable(),
});

export const RuleSettings = z.object({
  rounding: Rounding,
  minimumOrderCentavos: Centavos,
  quoteValidityDays: z.number().int().min(1).max(365),
  printHoursPerDay: z.number().int().min(1).max(24),
  annualRevenueLimitCentavos: z
    .number()
    .int()
    .min(100)
    .max(100_000_000_000)
    .describe('Annual revenue limit (MEI cap: R$ 81.000,00); alert at 80%.'),
});

export const Settings = z
  .object({
    business: BusinessSettings,
    costs: CostSettings,
    rules: RuleSettings,
    setup: z.object({ completedAt: IsoDateTime.nullable(), businessSkipped: z.boolean() }),
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'Settings' });
export type Settings = z.infer<typeof Settings>;

export const UpdateSettingsBody = z
  .object({
    business: BusinessSettings.partial().optional(),
    costs: CostSettings.partial().optional(),
    rules: RuleSettings.partial().optional(),
    reason: z
      .string()
      .trim()
      .min(3)
      .max(500)
      .optional()
      .describe('"Motivo da mudança": required when any cost parameter changes.'),
  })
  .meta({
    example: { costs: { energyTariffPerKwh: 0.95 }, reason: 'Reajuste da CELESC (+9,29%)' },
  });
export type UpdateSettingsBody = z.input<typeof UpdateSettingsBody>;

export const SettingChange = z.object({
  id: Uuid,
  at: IsoDateTime,
  param: z.string().describe('e.g. `costs.energyTariffPerKwh`, `rules.rounding`.'),
  oldValue: z.unknown(),
  newValue: z.unknown(),
  reason: z.string().nullable(),
  actor: z.string(),
});
export type SettingChange = z.infer<typeof SettingChange>;

export const SettingsDefaults = z
  .object({
    costs: CostSettings,
    rules: RuleSettings,
    bandeiraSuggestions: z
      .record(Bandeira, ReaisPerKwh.nullable())
      .describe('Suggested surcharge per flag, R$/kWh with taxes; null = check your bill.'),
  })
  .meta({ id: 'SettingsDefaults' });
export type SettingsDefaults = z.infer<typeof SettingsDefaults>;

/** pt-BR labels for the history panel and audit log. */
export const SETTING_LABELS: Record<string, string> = {
  'costs.energyTariffPerKwh': 'Tarifa de energia',
  'costs.bandeira': 'Bandeira',
  'costs.bandeiraPerKwh': 'Acréscimo da bandeira',
  'costs.laborRatePerHourCentavos': 'Acabamento e embalagem',
  'costs.designRatePerHourCentavos': 'Projeto',
  'costs.failureBps': 'Taxa de falha',
  'costs.wasteBps': 'Desperdício',
  'costs.targetMarginBps': 'Margem alvo',
  'costs.minimumMarginBps': 'Margem mínima',
  'costs.taxMode': 'Forma do imposto',
  'costs.dasMonthlyCentavos': 'DAS mensal',
  'costs.taxBps': 'Alíquota',
  'rules.rounding': 'Arredondamento',
  'rules.minimumOrderCentavos': 'Preço mínimo por pedido',
  'rules.quoteValidityDays': 'Validade do orçamento',
  'rules.printHoursPerDay': 'Horas de impressão por dia',
  'rules.annualRevenueLimitCentavos': 'Limite de faturamento anual',
};

// ---- Brand: logo and kit (Config-Negocio) -------------------------------------------------------

export const BRAND_KIT = [
  {
    slot: 'wm1',
    name: 'Marca d’água · selo',
    file: 'marca-dagua-selo-branco-300px.png',
    width: 300,
    height: 300,
    exact: false,
    use: 'fotos',
  },
  {
    slot: 'wm2',
    name: 'Marca d’água · horizontal branca',
    file: 'marca-dagua-horizontal-branca-800px.png',
    width: 800,
    height: 263,
    exact: false,
    use: 'fotos escuras',
  },
  {
    slot: 'wm3',
    name: 'Marca d’água · horizontal preta',
    file: 'marca-dagua-horizontal-preta-800px.png',
    width: 800,
    height: 263,
    exact: false,
    use: 'fotos claras',
  },
  {
    slot: 'ovf',
    name: 'Overlay feed',
    file: 'overlay-feed-1080x1350.png',
    width: 1080,
    height: 1350,
    exact: true,
    use: 'posts 4:5',
  },
  {
    slot: 'ovr',
    name: 'Overlay reels e stories',
    file: 'overlay-reels-stories-1080x1920.png',
    width: 1080,
    height: 1920,
    exact: true,
    use: 'vídeos verticais',
  },
  {
    slot: 'ovh',
    name: 'Overlay vídeo horizontal',
    file: 'overlay-video-horizontal-1920x1080.png',
    width: 1920,
    height: 1080,
    exact: true,
    use: 'vídeos 16:9',
  },
  {
    slot: 'enc',
    name: 'Encerramento de reels',
    file: 'encerramento-reels-1080x1920.png',
    width: 1080,
    height: 1920,
    exact: true,
    use: 'últimos 2 s do vídeo',
  },
] as const;
export const BrandKitSlot = z.enum(['wm1', 'wm2', 'wm3', 'ovf', 'ovr', 'ovh', 'enc']);
export type BrandKitSlot = z.infer<typeof BrandKitSlot>;

export const Brand = z
  .object({
    logo: z.object({
      custom: z.boolean().describe('false = the brand kit logo.'),
      url: z.string().describe('PNG; public (login screen).'),
      name: z.string(),
      uploadedAt: IsoDateTime.nullable(),
    }),
    kit: z.array(
      z.object({
        slot: BrandKitSlot,
        name: z.string(),
        file: z.string(),
        width: z.number().int(),
        height: z.number().int(),
        use: z.string(),
        custom: z.boolean(),
        replacedAt: IsoDateTime.nullable(),
        url: z.string(),
      }),
    ),
  })
  .meta({ id: 'Brand' });
export type Brand = z.infer<typeof Brand>;
